import {
  BankApplication,
  BankApplicationStatus,
  Query,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  SourceName,
  useApplicationTrackErrorEvents,
} from 'features/application/hooks/useApplicationTrackErrorEvents';
import Approved from 'features/signup/ApplicationStatus/Approved/Approved';
import Declined from 'features/signup/ApplicationStatus/Declined/Declined';
import Review from 'features/signup/ApplicationStatus/Review/Review';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import GetCustomerApplicationStatus from 'services/graphql/queries/GetCustomerApplicationStatus';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';
import { useQuery } from 'urql';

interface AccountApplicationScreenProps {
  bankApplicationStatus: Maybe<BankApplicationStatus>;
  bankApplicationId: BankApplication['id'];
  reExecuteQuery: () => void;
  isEvaluatingSocureCompletion: boolean;
  setIsEvaluatingSocureCompletion: (value: boolean) => void;
  handleSocureCompletedAnalytics: () => void;
  retryAttempts: number;
  setRetries: (value: number) => void;
  isReactivation: boolean;
  isReapplication: boolean;
  loaderText: {
    titleText: string;
    bodyText: string;
  };
}

const POLL_INTERVAL = 5000;
const RETRY_ATTEMPTS_LIMIT = 12;

function AccountApplicationScreen({
  retryAttempts,
  setRetries,
  bankApplicationStatus,
  bankApplicationId,
  reExecuteQuery,
  isEvaluatingSocureCompletion,
  setIsEvaluatingSocureCompletion,
  handleSocureCompletedAnalytics,
  isReactivation,
  isReapplication,
  loaderText,
}: AccountApplicationScreenProps): React.ReactNode {
  const [refetchTimeout, setRefetchTimeout] = useState<number>();

  if (bankApplicationStatus === null) {
    return null;
  }

  const waitForStatusChange = async (): Promise<void> => {
    for (let attempt = 0; attempt < RETRY_ATTEMPTS_LIMIT; attempt++) {
      await delay(POLL_INTERVAL);
      reExecuteQuery();
      const currentStatus = bankApplicationStatus;

      if (
        currentStatus &&
        currentStatus !== BankApplicationStatus.Pending &&
        currentStatus !== BankApplicationStatus.Incomplete
      ) {
        break;
      }
    }
  };

  const onSocureFlowCompleted = async () => {
    setIsEvaluatingSocureCompletion(true);
    await waitForStatusChange();
    handleSocureCompletedAnalytics();
    setIsEvaluatingSocureCompletion(false);
  };

  if (isEvaluatingSocureCompletion) {
    return <FullScreenLoading isCoinLoader />;
  }

  switch (bankApplicationStatus) {
    case BankApplicationStatus.Approved:
      return (
        <Approved
          isReactivation={isReactivation}
          isReapplication={isReapplication}
        />
      );
    case BankApplicationStatus.Incomplete:
    case BankApplicationStatus.Pending:
      if (retryAttempts < RETRY_ATTEMPTS_LIMIT) {
        if (!refetchTimeout) {
          clearTimeout(refetchTimeout);
          setRefetchTimeout(
            window.setTimeout(() => {
              setRefetchTimeout(undefined);
              reExecuteQuery();
              setRetries(retryAttempts + 1);
            }, POLL_INTERVAL),
          );
        }
        return (
          <FullScreenLoading
            isCoinLoader
            titleText={loaderText.titleText}
            bodyText={loaderText.bodyText}
          />
        );
      }
      return (
        <Review
          bankApplicationStatus={bankApplicationStatus}
          bankApplicationId={bankApplicationId}
          onSocureFlowCompleted={onSocureFlowCompleted}
          isReactivation={isReactivation}
          isReapplication={isReapplication}
        />
      );
    case BankApplicationStatus.Declined:
      return (
        <Declined
          isReactivation={isReactivation}
          isReapplication={isReapplication}
        />
      );
    case BankApplicationStatus.InReview:
      return (
        <Review
          bankApplicationStatus={bankApplicationStatus}
          bankApplicationId={bankApplicationId}
          onSocureFlowCompleted={onSocureFlowCompleted}
          isReactivation={isReactivation}
          isReapplication={isReapplication}
        />
      );
    default:
      return <FullScreenLoading isCoinLoader />;
  }
}

const ApplicationStatus = () => {
  const router = useRouter();
  const [retryAttempts, setRetries] = useState(0);
  const [isEvaluatingSocureCompletion, setIsEvaluatingSocureCompletion] =
    useState(false);
  const { logErrorEvent } = useApplicationTrackErrorEvents();
  const { sourceName, isReapplicationFlow } = useLocalSearchParams();

  const isReactivation =
    sourceName === SourceName.REACTIVATION ||
    sourceName === SourceName.REACTIVATION_LEGAL_AGREEMENTS;
  const isReapplication = isReapplicationFlow === 'true';
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'loadingScreen',
  });
  const [response, reExecuteQuery] = useQuery<Query>({
    query: GetCustomerApplicationStatus,
    requestPolicy: 'network-only',
  });

  const bankStatus = response?.data?.applications.find(
    (key) =>
      key &&
      '__typename' in key &&
      key.__typename === Typenames.BankApplication,
  ) as BankApplication;

  const errorMessage = response?.error?.message;

  useEffect(() => {
    if (errorMessage) {
      logErrorEvent(
        errorMessage,
        isReactivation
          ? SourceName.REACTIVATION
          : SourceName.APPLICATION_DECISION,
      );
    }
  }, [errorMessage, logErrorEvent, isReactivation]);

  const handleSocureCompletedAnalytics = usePressableAnalytics({
    analyticsId: 'idv-completed',
    customAttributes: {
      vendor: 'Socure',
      ...(isReactivation && { reactivation_flow: 'true' }),
      ...(isReapplication && { reapplication_flow: 'true' }),
    },
  });

  const handleBackButtonAction = useCallback(() => {
    if (isReactivation) {
      router.replace('/reactivation');
    } else {
      router.replace('/login');
    }
  }, [isReactivation, router]);

  useBackHandler(handleBackButtonAction);

  const isLoading =
    response.fetching && bankStatus?.bankApplicationStatus === undefined;

  return (
    <View style={styles.container}>
      <View style={styles.outerContainer}>
        <Stack.Screen
          options={{
            headerBackVisible: true,
            headerShown: false,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                {...props}
                primaryOnPress={handleBackButtonAction}
              />
            ),
          }}
        />

        {isLoading ? (
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('title')}
            bodyText={translatedText('description')}
          />
        ) : (
          <AccountApplicationScreen
            bankApplicationStatus={bankStatus?.bankApplicationStatus}
            bankApplicationId={bankStatus?.id}
            reExecuteQuery={reExecuteQuery}
            isEvaluatingSocureCompletion={isEvaluatingSocureCompletion}
            setIsEvaluatingSocureCompletion={setIsEvaluatingSocureCompletion}
            handleSocureCompletedAnalytics={handleSocureCompletedAnalytics}
            isReactivation={isReactivation}
            isReapplication={isReapplication}
            retryAttempts={retryAttempts}
            setRetries={setRetries}
            loaderText={{
              titleText: translatedText('title'),
              bodyText: translatedText('description'),
            }}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  outerContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
  },
});

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export default ApplicationStatus;
